import {
  Viewer,
  Cesium3DTileset,
  Resource,
  Cesium3DTileFeature,
  Cesium3DTileColorBlendMode,
  Cesium3DTileStyle,
  Cesium3DTile,
  HeightReference,
  Scene,
} from "cesium";
import { DeferredValue, Guard } from "lib/actions/util";
import { AttachablePluginInterface } from "./CesiumWrapper";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";

type TilesetState = {
  id: string;
  status: "loading" | "ready";
  tileset: Cesium3DTileset | null;
  isVector: boolean;
  parentTilesetId: string | null;
};

export type TilesetLoadOptions = {
  isPointCloud?: boolean;
  isVector?: boolean;
  /**
   * Id of the tileset this one was loaded beside, whose style rules also apply
   * here (but not the reverse). This tileset's features are reported under that
   * id, so a picked or deep-linked line belongs to its asset.
   */
  parentTilesetId?: string;
  /**
   * Clamp the tileset to terrain unless its metadata says the source had Z
   * (`has_z`). A tileset with no `has_z` is 2D.
   */
  isDrapedUnlessSourceHasZ?: boolean;
};

export type TilesetFeature = Pick<
  Cesium3DTileFeature,
  "tileset" | "getProperty" | "getPropertyIds"
>;

type TileVisibleEvent = {
  tilesetId: string;
  tileset: Cesium3DTileset;
  tile: Cesium3DTile;
};

export default class TilesetPlugin implements AttachablePluginInterface {
  private readonly _viewer = new DeferredValue<Viewer>();
  private _state: { [id: string]: TilesetState } = {};
  private _tileVisibleEvent = new EventEmitter<TileVisibleEvent>();
  private _tileVisibleCleanups = new Map<string, () => void>();

  get tileVisible(): EventEmitterInterface<TileVisibleEvent> {
    return this._tileVisibleEvent;
  }

  static getTilesetIdFromTileset(tileset: Cesium3DTileset) {
    Guard.require(tileset, "tileset");
    return tileset.extras?.tilesetId as string | null;
  }

  static isFeature(picked: unknown): picked is TilesetFeature {
    if (picked instanceof Cesium3DTileFeature) return true;
    // Cesium3DTileVectorFeature is not exported by cesium, so vector picks are
    // recognised by shape. A whole-tileset pick has a primitive but no
    // getProperty.
    const candidate = picked as
      | { primitive?: unknown; getProperty?: unknown }
      | undefined;
    if (!(candidate?.primitive instanceof Cesium3DTileset)) return false;
    return typeof candidate.getProperty === "function";
  }

  static getElementIdFromFeature(feature: TilesetFeature) {
    Guard.require(feature, "feature");
    Guard.require(feature.getProperty, "feature.getProperty");
    // Try to get 'id' first, then fallback to 'GUID' for backward compatibility
    const id = feature.getProperty("id") as string | undefined;
    if (id) return id;
    const guid = feature.getProperty("GUID") as string | undefined;
    return guid ?? null;
  }

  setViewer(viewer: Viewer) {
    this._viewer.set(viewer);
  }

  detachViewer() {
    this._tileVisibleCleanups.forEach((cleanup) => cleanup());
    this._tileVisibleCleanups.clear();
    this._state = {};
    this._viewer.clear();
  }

  get(id: string) {
    Guard.require(id, "id");
    return this.getState(id)?.tileset ?? null;
  }

  getAll() {
    return Object.values(this._state);
  }

  isExisting(id: string) {
    Guard.require(id, "id");
    return !!this.getState(id);
  }

  isReady(id: string) {
    Guard.require(id, "id");
    return this.getState(id)?.status === "ready";
  }

  isShowing(id: string) {
    Guard.require(id, "id");
    return !!this.get(id)?.show;
  }

  async load(id: string, resource: Resource, options: TilesetLoadOptions = {}) {
    Guard.require(id, "id");
    Guard.require(resource, "resource");
    if (this.getState(id)) return;

    const viewer = await this._viewer.getValueWhenReady();
    if (!viewer) return;

    const isVector = !!options.isVector;
    const parentTilesetId = options.parentTilesetId ?? null;
    this.setState(id, {
      id,
      tileset: null,
      status: "loading",
      isVector,
      parentTilesetId,
    });
    try {
      const tileset = await this.createTileset(
        id,
        resource,
        viewer.scene,
        options,
      );
      viewer.scene.primitives.add(tileset);
      this.setState(id, {
        id,
        tileset,
        status: "ready",
        isVector,
        parentTilesetId,
      });
      viewer.scene.requestRender();
    } catch (e) {
      delete this._state[id];
      throw e;
    }
  }

  show(id: string) {
    Guard.require(id, "id");
    const state = this.getState(id);
    if (state?.tileset) {
      state.tileset.show = true;
      this._viewer.current?.scene.requestRender();
    }
  }

  hide(id: string) {
    Guard.require(id, "id");
    const state = this.getState(id);
    if (state?.tileset) {
      state.tileset.show = false;
      this._viewer.current?.scene.requestRender();
    }
  }

  private getState(id: string): TilesetState | null {
    return this._state[id] ?? null;
  }

  private setState(id: string, value: TilesetState) {
    this._state[id] = value;
  }

  private async createTileset(
    id: string,
    resource: Resource,
    scene: Scene,
    options: TilesetLoadOptions,
  ) {
    const heightOptions = options.isDrapedUnlessSourceHasZ
      ? await this.getHeightReferenceOptions(resource, scene)
      : {};
    const tileset = await Cesium3DTileset.fromUrl(resource, {
      show: true,
      ...heightOptions,
    });
    const featureOwnerTilesetId = options.parentTilesetId ?? id;

    const handleTileVisible = (tile: Cesium3DTile) => {
      if (this._tileVisibleEvent.hasHandlers) {
        this._tileVisibleEvent.emit({
          tilesetId: featureOwnerTilesetId,
          tileset,
          tile,
        });
      }
    };

    const cleanupTileVisible =
      tileset.tileVisible.addEventListener(handleTileVisible);
    this._tileVisibleCleanups.set(id, cleanupTileVisible);

    tileset.colorBlendMode = Cesium3DTileColorBlendMode.REPLACE;
    if (!tileset.extras) (tileset as any)._extras = {};
    Object.assign(tileset.extras, {
      tilesetId: featureOwnerTilesetId,
    });

    if (options.isPointCloud)
      tileset.style = new Cesium3DTileStyle({ pointSize: "3.0" });
    return tileset;
  }

  /**
   * The height reference is fixed when a tileset is constructed, so its
   * metadata is read from the tileset document first. Clamped vector content is
   * drawn by the scene's globe, which is why the scene is passed too.
   */
  private async getHeightReferenceOptions(resource: Resource, scene: Scene) {
    const tilesetDocument = await resource.fetchJson();
    const hasZ = tilesetDocument?.metadata?.properties?.has_z === true;
    if (hasZ) return {};

    return { heightReference: HeightReference.CLAMP_TO_TERRAIN, scene };
  }
}
